import React, { useEffect, useState } from "react";
import { apiClient } from "../../lib/api";
// Componentes
import UserStatsStrip from "./components/UserStatsStrip";
import UserFilterToolbar from "./components/UserFilterToolbar";
import UserGrid from "./components/UserGrid";
import UserDetailDrawer from "./components/UserDetailDrawer";
import UserActionModal from "./components/UserActionModal";

// ----------------------------------------------------------------------
// 1. Interfaces
// ----------------------------------------------------------------------
export interface User {
  userId: number;
  name: string;
  lastName: string;
  photoLink: string;
  email: string | null;
  phoneNumber: string | null;
  phoneCode: string | null;
  status: string;
  role?: string;
  roleLabel?: string;

  roles?: string[];

  registerDate?: string;
  isSuperHost?: boolean;
  avatarIniciales?: string;
  reservas?: number;
  monto?: number;
  tipoMonto?: string;
  rating?: number | null;
  ciudad?: string | null;
  ultimoAcceso?: string;
  verifEmail?: string;
  verifTelefono?: string;
  verifIdentidad?: string;
}

export interface ApiResponse {
  pagination: {
    totalItemCount: number;
    totalPageCount: number;
    pageSize: number;
    currentPage: number;
    hasPrevious: boolean;
    hasNext: boolean;
  };
  data: any[];
}

export interface UserStats {
  totalUsers: number;
  activeToday: number;
  guests: number;
  hosts: number;
  superHosts: number;
  suspendedUsers: number;
}

// NUEVAS INTERFACES PARA LA CONFIGURACIÓN DE SUSPENSIÓN
export interface SuspensionCurrentBlock {
  roleCode: string;
  roleLabel: string;
  isBlocked: boolean;
  blockType: string | null;
  startDate: string | null;
  endDate: string | null;
  reason: string | null;
}

export interface SuspensionConfigData {
  user: any;
  currentBlocks: SuspensionCurrentBlock[];
  config: {
    roles: { key: string; label: string }[];
    suspensionTypes: { id: string; label: string; description: string; allowsDuration: boolean }[];
    durationOptions: { value: number; label: string }[];
  };
}

// Exportar el helper para que lo usen los componentes hijos
export const getInitials = (name: string, lastName: string) => {
  return `${name?.charAt(0) || ""}${lastName?.charAt(0) || ""}`.toUpperCase();
};

const UsuariosPageNew = () => {
  // ESTADOS
  const [activeFilter, setActiveFilter] = useState("todos");
  const [sortOrder, setSortOrder] = useState("recent");
  const [usuarios, setUsuarios] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState<ApiResponse["pagination"] | null>(null);

  const [stats, setStats] = useState<UserStats | null>(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isDrawerLoading, setIsDrawerLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [actionType, setActionType] = useState<"status" | "superhost">("status");

  // SE CAMBIARON A LOS TIPOS QUE DEVUELVE EL BACKEND
  const [suspendType, setSuspendType] = useState<string>("temp");
  const [suspendDuration, setSuspendDuration] = useState<number>(3);
  const [suspendReason, setSuspendReason] = useState("");
  const [reasonError, setReasonError] = useState(false);
  const [roleToSuspend, setRoleToSuspend] = useState<string>("both");

  // NUEVO ESTADO PARA GUARDAR LA CONFIGURACIÓN
  const [suspensionConfig, setSuspensionConfig] = useState<SuspensionConfigData | null>(null);

  const [toast, setToast] = useState<{ msg: string; type: "success" | "danger" | "info" | "gold" } | null>(null);

  const filters = [
    { id: "todos", label: "Todos", queryRole: "" },
    { id: "huespedes", label: "Huéspedes", queryRole: "guest" },
    { id: "anfitriones", label: "Anfitriones", queryRole: "host" },
    { id: "super", label: "⭐ Super Anfitrión", queryRole: "superHost" },
    { id: "suspendidos", label: "Suspendidos", queryRole: "suspended" },
  ];
  const currentFilter = filters.find((f) => f.id === activeFilter);

  // LOGICA API
  const fetchStats = async () => {
    try {
      const response = await apiClient.get<any>("GetUserStats");
      if (response?.data?.userStats) {
        setStats(response.data.userStats);
      }
    } catch (error) { console.error("Error cargando estadísticas:", error); }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        pageNumber: currentPage.toString(),
        pageSize: "20",
        orderBy: sortOrder
      });

      if (currentFilter && currentFilter.queryRole) params.append("roleFilter", currentFilter.queryRole);
      if (searchTerm.trim()) params.append("search", searchTerm.trim());

      const endpoint = `GetUserCards?${params.toString()}`;
      const response = await apiClient.get<ApiResponse>(endpoint);

      const mappedUsers: User[] = (response.data || []).map((item: any) => {

        let mappedStatus = "BL";
        if (item.estado && item.estado.toLowerCase() === "activo") mappedStatus = "AC";

        let mappedRole = "Guest";
        if (item.rol === "Anfitrión") mappedRole = "Host";

        return {
          userId: item.userId,
          name: item.name ? item.name.trim() : "",
          lastName: item.lastName ? item.lastName.trim() : "",
          photoLink: item.photoLink || "",
          email: item.email || null,
          phoneNumber: null,
          phoneCode: null,
          status: mappedStatus,
          role: mappedRole,
          roleLabel: item.rol,
          roles: item.roles || [],
          registerDate: undefined,
          isSuperHost: item.superAnfitrion || false,
          reservas: item.reservas || 0,
          monto: item.monto || 0,
          tipoMonto: item.tipoMonto || "Gastado",
          rating: item.rating || null,
        };
      });

      setUsuarios(mappedUsers);
      setPagination(response.pagination);
    } catch (error) {
      console.error("Error cargando usuarios:", error);
      setUsuarios([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchUsers(); }, [activeFilter, currentPage, searchTerm, sortOrder]);
  useEffect(() => { fetchStats(); }, []);

  // HANDLERS
  const handleFilterChange = (filterId: string) => {
    setActiveFilter(filterId);
    setCurrentPage(1);
    setIsDrawerOpen(false);
  };

  const openActionModal = async (user: User, type: "status" | "superhost" = "status", e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setSelectedUser(user);
    setActionType(type);
    setReasonError(false);
    setSuspendReason("");

    if (type === "status") {
      setIsUpdating(true); // Mostrar que estamos cargando
      try {
        const response = await apiClient.get<any>(`GetUserSuspensionConfig/${user.userId}`);
        const configData = response.data?.data || response.data;

        if (configData && configData.config) {
          setSuspensionConfig(configData);
          
          // Setear valores por defecto dinámicos
          if (configData.config.suspensionTypes?.length > 0) setSuspendType(configData.config.suspensionTypes[0].id);
          if (configData.config.durationOptions?.length > 0) setSuspendDuration(configData.config.durationOptions[0].value);
          
          const hasMultipleRoles = user.roles && user.roles.includes("Host") && user.roles.includes("Guest");
          setRoleToSuspend(hasMultipleRoles ? "both" : (user.roles?.includes("Host") ? "host" : "guest"));
        }
      } catch (error) {
        showToast("Error al obtener la configuración de suspensión del usuario.", "danger");
        setIsUpdating(false);
        return; // Abortamos abrir el modal si falla
      }
      setIsUpdating(false);
    }

    setIsModalOpen(true);
    setIsDrawerOpen(false);
  };

  const openDrawer = async (user: User) => {
    setSelectedUser(user);
    setIsDrawerOpen(true);
    if (!user.userId) return;

    setIsDrawerLoading(true);
    try {
      const response = await apiClient.get<any>(`${user.userId}/GetCardInfo`);
      const details = response.data;

      if (details) {
        setSelectedUser((prev) => {
          if (!prev) return null;
          let rawDate = details.informacionPersonal?.miembroDesde || prev.registerDate;
          if (rawDate && rawDate.length === 10 && rawDate.includes("-")) {
            rawDate = rawDate.replace(/-/g, '/');
          }
          return {
            ...prev,
            photoLink: details.usuario?.profileImage || prev.photoLink,
            email: details.usuario?.email || prev.email,
            roleLabel: details.usuario?.rol || prev.roleLabel,
            phoneNumber: details.informacionPersonal?.telefono || prev.phoneNumber,
            ciudad: details.informacionPersonal?.ciudad || "N/A",
            registerDate: rawDate,
            ultimoAcceso: details.informacionPersonal?.ultimoAcceso || "N/A",
            reservas: details.actividad?.reservas || prev.reservas,
            monto: details.actividad?.totalGastado || prev.monto,
            rating: details.actividad?.rating || prev.rating,
            verifEmail: details.verificacionIdentidad?.email || "No verificado",
            verifTelefono: details.verificacionIdentidad?.telefono || "No verificado",
            verifIdentidad: details.verificacionIdentidad?.identidad || "No verificado",
          };
        });
      }
    } catch (error) { showToast("No se pudo cargar el detalle del usuario", "danger"); }
    finally { setIsDrawerLoading(false); }
  };

  const showToast = (msg: string, type: "success" | "danger" | "info" | "gold" = "info") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 5000);
  }

  const confirmAction = async () => {
    if (!selectedUser || isUpdating) return;

    const isSuspending = actionType === "status" && selectedUser.status === "AC";
    if (isSuspending && !suspendReason.trim()) {
      setReasonError(true);
      return;
    }

    setIsUpdating(true);
    try {
      if (actionType === "status") {
        const isCurrentlyActive = selectedUser.status === "AC";

        const executeStatusChange = async (targetRole: "Host" | "Guest") => {
          const action = isCurrentlyActive ? "Block" : "Unblock";
          const url = `User/${selectedUser.userId}/${action}As${targetRole}`;

          const payload = isCurrentlyActive ? {
            blockType: suspendType,
            duration: suspendType === "temp" ? suspendDuration : null,
            reason: suspendReason
          } : {}; 

          await apiClient.put(url, payload);
        };

        if (activeFilter === "huespedes") {
          await executeStatusChange("Guest");
        } else if (activeFilter === "anfitriones" || activeFilter === "super") {
          await executeStatusChange("Host");
        } else {
          if (roleToSuspend === "both") {
            if (selectedUser.roles?.includes("Guest")) await executeStatusChange("Guest");
            if (selectedUser.roles?.includes("Host")) await executeStatusChange("Host");
          } else if (roleToSuspend === "host") {
            await executeStatusChange("Host");
          } else {
            await executeStatusChange("Guest");
          }
        }

        showToast(
          isCurrentlyActive ? `Cuenta suspendida` : "Cuenta reactivada",
          isCurrentlyActive ? "danger" : "success"
        );
      } else {
        const currentStatus = selectedUser.isSuperHost ?? false;
        const action = currentStatus ? "DeactivateSuperHost" : "ActivateSuperHost";
        await apiClient.put(`User/${selectedUser.userId}/${action}`, {});
        showToast(currentStatus ? "Distinción removida" : "⭐ Super Anfitrión otorgado", currentStatus ? "info" : "gold");
      }
      setIsModalOpen(false);
      fetchUsers();
      fetchStats();
    } catch (error) {
      showToast("Error al procesar la acción.", "danger");
    }
    finally { setIsUpdating(false); }
  };

  return (
    <div className="flex w-full h-screen bg-gray-50 text-gray-900 font-sans overflow-hidden">
      <div className="flex-1 flex flex-col overflow-hidden">

        {/* TOPBAR */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center px-4 md:px-[32px] py-4 md:py-[20px] border-b border-gray-200 bg-white sticky top-0 z-10 gap-4 sm:gap-0">
          <div>
            <h1 className="text-[20px] font-bold flex items-center gap-[10px] m-0 text-gray-900">
              <svg width="22" height="22" fill="none" stroke="#4f6df5" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              Usuarios
            </h1>
            <p className="text-xs text-gray-500 mt-1 m-0">
              {stats ? `${stats.totalUsers} usuarios registrados en la plataforma` : "Cargando..."}
            </p>
          </div>
          <div className="flex items-center gap-2.5">
            {/* <button className="text-xs font-semibold px-4 py-2 rounded-lg bg-[#4f6df5] text-white flex items-center gap-1.5 hover:bg-[#3b5bdb] transition-colors shadow-sm">
              <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
              Nuevo usuario
            </button> */}
          </div>
        </div>

        <UserStatsStrip stats={stats} />

        <UserFilterToolbar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          filters={filters}
          activeFilter={activeFilter}
          onFilterChange={handleFilterChange}
          sortOrder={sortOrder}
          onSortChange={(val) => { setSortOrder(val); setCurrentPage(1); }}
        />

        <div className="flex-1 overflow-y-auto p-6 md:p-7 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
          <UserGrid
            usuarios={usuarios}
            loading={loading}
            onUserClick={openDrawer}
            onActionClick={openActionModal}
          />

          {pagination && pagination.totalPageCount > 1 && (
            <div className="flex justify-center items-center gap-3 mt-8 pt-5 border-t border-gray-200">
              <button
                disabled={!pagination.hasPrevious}
                onClick={() => setCurrentPage((p) => p - 1)}
                className="bg-white border border-gray-300 text-gray-700 px-3 py-1.5 rounded-md text-xs font-semibold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-colors"
              >
                Anterior
              </button>
              <span className="text-xs text-gray-500 font-medium">Página {pagination.currentPage} de {pagination.totalPageCount}</span>
              <button
                disabled={!pagination.hasNext}
                onClick={() => setCurrentPage((p) => p + 1)}
                className="bg-white border border-gray-300 text-gray-700 px-3 py-1.5 rounded-md text-xs font-semibold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-colors"
              >
                Siguiente
              </button>
            </div>
          )}
        </div>
      </div>

      <UserDetailDrawer
        selectedUser={selectedUser}
        isDrawerOpen={isDrawerOpen}
        isDrawerLoading={isDrawerLoading}
        activeFilter={activeFilter}
        onClose={() => setIsDrawerOpen(false)}
        onActionClick={openActionModal}
      />

      <UserActionModal
        isOpen={isModalOpen}
        selectedUser={selectedUser}
        actionType={actionType}
        isUpdating={isUpdating}
        
        suspensionConfig={suspensionConfig} // Prop nueva
        
        suspendType={suspendType}
        setSuspendType={(val: string) => setSuspendType(val)}
        suspendDuration={suspendDuration}
        setSuspendDuration={(val: number) => setSuspendDuration(val)}
        suspendReason={suspendReason}
        setSuspendReason={setSuspendReason}
        reasonError={reasonError}
        setReasonError={setReasonError}
        roleToSuspend={roleToSuspend}
        setRoleToSuspend={(val: string) => setRoleToSuspend(val)}
        activeFilter={activeFilter}
        onClose={() => setIsModalOpen(false)}
        onConfirm={confirmAction}
      />

      {/* TOAST */}
      <style>{`@keyframes toastIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }`}</style>
      {toast && (
        <div className="fixed bottom-6 right-6 z-[200] bg-white rounded-[10px] text-[13px] font-semibold py-3 px-5 shadow-lg border"
          style={{
            animation: "toastIn 0.3s ease forwards",
            borderColor: toast.type === "success" ? "#34d399" : toast.type === "danger" ? "#f87171" : toast.type === "gold" ? "#f59e0b" : "#e5e7eb",
            color: toast.type === "success" ? "#059669" : toast.type === "danger" ? "#dc2626" : toast.type === "gold" ? "#d97706" : "#1f2937"
          }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
};

export default UsuariosPageNew;